import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import toast from 'react-hot-toast';

// material-ui
import { Typography } from '@material-ui/core';

// project imports
import NavGroup from './NavGroup';
import { buildMenu } from './../../../../menu-items';
import { getProject } from '../../../../views/projects/helper';
import {
    getPinnedProjects,
    pinProject,
    unpinProject,
    ACTIVE_PROJECT_EVENT,
} from '../../../../utils/activeProject';

const MenuList = ({ session_user }) => {
    const userId = session_user?.id || session_user?._id || null;
    const projectMatch = useRouteMatch('/project/:projectId');
    const urlProjectId = projectMatch?.params?.projectId || null;
    const history = useHistory();

    const [pinned, setPinned] = React.useState(() => (userId ? getPinnedProjects(userId) : []));

    // When the logged-in user changes, reset the pin list to that user's bucket.
    React.useEffect(() => {
        setPinned(userId ? getPinnedProjects(userId) : []);
    }, [userId]);

    // Cross-component sync (dashboard delete/rename, etc).
    React.useEffect(() => {
        const handler = () => setPinned(userId ? getPinnedProjects(userId) : []);
        window.addEventListener(ACTIVE_PROJECT_EVENT, handler);
        return () => window.removeEventListener(ACTIVE_PROJECT_EVENT, handler);
    }, [userId]);

    // On login / user change: validate all pinned projects and remove any this account can't open.
    // Only a definitive backend answer unpins — 404 (deleted) or 403 (another account's project,
    // e.g. pinned by a tab left open on it across an account switch). Any other failure (network
    // blip, 401 mid-refresh, 500) must NOT unpin, or a transient error while offline silently
    // drops a project that's actually still there (fetchHelper marks "no response received"
    // errors with code 0, specifically so they're distinguishable from a real 404 here).
    React.useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        const list = getPinnedProjects(userId);
        if (!list.length) return;
        Promise.all(
            list.map((p) =>
                getProject(p.id)
                    .then((proj) => ({ id: p.id, name: proj?.name || p.name, confirmedGone: false }))
                    .catch((err) => ({ id: p.id, confirmedGone: err?.code === 404 || err?.code === 403 }))
            )
        ).then((results) => {
            if (cancelled) return;
            results.forEach((r) => {
                if (r.confirmedGone) {
                    unpinProject(userId, r.id);
                } else {
                    // r.name was fetched fresh above but never written back — if the project
                    // was renamed from elsewhere (another tab, an admin), the pinned sidebar
                    // label kept showing the old name until the user visited it directly
                    // (which re-pins via the effect below) or logged out and back in.
                    // pinProject() itself no-ops if the name is already up to date.
                    pinProject(userId, { id: r.id, name: r.name });
                }
            });
        });
        return () => { cancelled = true; };
    }, [userId]);

    // When the URL points to a project, pin it for the current user — once the backend confirms
    // this account can open it. Pinning first (under a placeholder "Project" name) put another
    // account's project into this account's sidebar whenever a tab left open on it reloaded after
    // an account switch, and a 403 never unpinned it: an empty "Project" that appeared on its own.
    React.useEffect(() => {
        let cancelled = false;
        if (!urlProjectId || !userId) return;
        getProject(urlProjectId)
            .then((p) => {
                if (cancelled) return;
                pinProject(userId, { id: urlProjectId, name: p?.name || 'Project' });
            })
            .catch((err) => {
                if (cancelled) return;
                // Same rule as the validation effect above: only a definitive 404 / 403 unpins and
                // leaves the page (it would only show empty lists) — a transient failure here
                // shouldn't silently drop the project from the sidebar.
                if (err?.code === 404 || err?.code === 403) {
                    unpinProject(userId, urlProjectId);
                    toast.error(err.code === 404
                        ? 'This project no longer exists.'
                        : 'This project belongs to another account — it isn\'t available to the account you\'re signed in with.');
                    history.replace('/dashboard');
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlProjectId, userId]);

    // The pinned group is the only sidebar nav to a project's Survey Templates/Files pages —
    // there's no in-page tab bar. Unpinning the project currently being viewed would remove
    // those links while the user is still on one of its pages, so redirect to Dashboard in that
    // case rather than leaving the user stranded on a page with no way back to it.
    const handleDismiss = (id) => () => {
        const wasViewingThis = String(id) === String(urlProjectId);
        unpinProject(userId, id);
        if (wasViewingThis) history.push('/dashboard');
    };

    const projectMenuItems = pinned.map((p) => ({
        id: p.id,
        name: p.name,
        onDismiss: handleDismiss(p.id),
    }));

    const menu = buildMenu({
        role: session_user?.role,
        projects: projectMenuItems,
        email: session_user?.email,
    });

    return menu.items.map((item) => {
        switch (item.type) {
            case 'group':
                return <NavGroup key={item.id} item={item} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });
};

const mapStateToProps = (state) => ({
    session_user: state.authReducer.user,
});

export default connect(mapStateToProps, null)(MenuList);
